import { inject, Injectable, NgZone }                         from '@angular/core';
import { MatDialog }                                          from '@angular/material/dialog';
import { Router }                                             from '@angular/router';
import { Observable, of, Subscription, timer }                from 'rxjs';
import { catchError, delay, map, mergeMap, shareReplay, tap } from 'rxjs/operators';

import { GrantType, SsoService }                       from './sso.service';
import { AuthService, RegistrationData, SUCCESS_TYPE } from './auth.service';
import { UserService }                                 from '../../api/user/user.service';
import { AccountDataService }                          from 'src/app/api/account-data/account-data.service';
import { PingOneIdentityService }                      from '../aws-cognito/ping-one-identity.service';
import { TimeoutService }                              from '../session/timeout/timeout.service';
import { LoginResult }                                 from './data/login-result';
import { SessionChangedData }                          from './data/session-changed-data';
import { environment }                                 from '../../../environments/environment';
import { CacheUtils }                                  from '../../shared/utils/cache.utils';
import { BroadCastService, DcpCrossWindowMessage }     from '../broadcast.service';
import { TimeUtils }                                   from '../../shared/utils/time.utils';
import { IframeService }                               from '../iframe.service';

export const sessionMetadata: SessionMetadata = {
  advisedWindowUuids: []
} as SessionMetadata;

const sessionRenewalThreshold = environment.sessionThresholdRenewalTime;
const multicastWaitTime = environment.sessionRefreshMulticastWaitTime;

enum SessionEvent {
  LOGIN_COMPLETE = 'LOGIN_COMPLETE',
  REFRESH_INTENTION = 'REFRESH_INTENTION',
  REFRESH_STARTED = 'REFRESH_STARTED',
  REFRESH_COMPLETE = 'REFRESH_COMPLETE',
  LOGOUT_COMPLETE = 'LOGOUT_COMPLETE',
  LOGOUT_SESSION_EXPIRED = 'LOGOUT_SESSION_EXPIRED',
  INSTANT_LOGIN_RELOAD = 'INSTANT_LOGIN_RELOAD'
}

interface SessionMetadata {
  refreshTriggerAt: number;
  tokenExpiresAt: number;
  refreshTimer?: Subscription;
  isSso: boolean;
  advisedWindowUuids: Array<string>,
  changesInProgress?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class AuthenticationService implements AuthService {
  private vendorAuthService = inject(PingOneIdentityService);
  private ssoService = inject(SsoService);
  private accDataService = inject(AccountDataService);
  private userService = inject(UserService);
  private dialog = inject(MatDialog);
  private router = inject(Router);
  private timeoutService = inject(TimeoutService);
  private broadCastService = inject(BroadCastService);
  private zone = inject(NgZone);

  private pendingAuthenticationCheck$: Observable<SessionChangedData> | null = null;

  constructor() {
    this.initializeBroadcastChannelListeners();
    this.isAuthenticated().subscribe();
  }

  login(username: string, password: string): Observable<LoginResult> {
    return this.interceptSessionChangeAndStartTimer(
      this.vendorAuthService.login(username, password),
      SessionEvent.LOGIN_COMPLETE);
  }

  ssoLogin(value: string, pingAppName: string, grantType: GrantType): Observable<LoginResult> {
    return this.interceptSessionChangeAndStartTimer(
      this.ssoService.ssoLogin(value, pingAppName, grantType),
      SessionEvent.LOGIN_COMPLETE);
  }

  associateSoftwareToken(): Observable<Record<'secretCode' | 'qrValue', string>> {
    return this.vendorAuthService.associateSoftwareToken();
  }

  changePassword(oldPassword: string, newPassword: string): Observable<SUCCESS_TYPE> {
    return this.vendorAuthService.changePassword(oldPassword, newPassword);
  }

  recoverPassword(verificationCode: string, newPassword: string, userName: string): Observable<SUCCESS_TYPE> {
    return this.vendorAuthService.recoverPassword(verificationCode, newPassword, userName);
  }

  confirmRegistration(username: string, confirmationCode: string): Observable<SUCCESS_TYPE> {
    return this.vendorAuthService.confirmRegistration(username, confirmationCode);
  }

  disableMfa(): Observable<string> {
    return this.vendorAuthService.disableMfa();
  }

  forgotPassword(username: string): Observable<SUCCESS_TYPE> {
    return this.vendorAuthService.forgotPassword(username);
  }

  resendRecoveryCode(username: string): Observable<SUCCESS_TYPE> {
    return this.vendorAuthService.resendRecoveryCode(username);
  }

  forgotUsername(email: string): Observable<SUCCESS_TYPE> {
    return this.vendorAuthService.forgotUsername(email);
  }

  getUserMfaOptions(): Observable<string[]> {
    return this.vendorAuthService.getUserMfaOptions();
  }

  isAuthenticated(): Observable<SessionChangedData> {
    if (this.pendingAuthenticationCheck$) {
      return this.pendingAuthenticationCheck$;
    }

    const check$ = this.interceptSessionChangeAndStartTimer(
      this.vendorAuthService.isAuthenticated(),
      null
    ).pipe(shareReplay({bufferSize: 1, refCount: false}));

    this.pendingAuthenticationCheck$ = check$;

    const clearPending = () => this.pendingAuthenticationCheck$ = null;
    check$.subscribe({next: clearPending, error: clearPending});

    return check$;
  }

  logout(sessionExpired = false): Observable<unknown> {
    return this.interceptSessionChangeAndStartTimer(
      this.userService.revokeJWT()
        .pipe(
          catchError(err => {
            console.error('JWT revocation failed', err);
            return of(true);
          }),
          map(() => <SessionChangedData>{})
        ),
      !sessionExpired ? SessionEvent.LOGOUT_COMPLETE : SessionEvent.LOGOUT_SESSION_EXPIRED
    );
  }

  refreshSession(): Observable<SessionChangedData> {
    return this.interceptSessionChangeAndStartTimer(
      this.getApplicableService().pipe(mergeMap(service => service.refreshSession())),
      SessionEvent.REFRESH_COMPLETE);
  }

  notifyMfaSetup(): Observable<SUCCESS_TYPE> {
    return this.vendorAuthService.notifyMfaSetup();
  }

  notifyEmailMfaSetup(): Observable<SUCCESS_TYPE> {
    return this.vendorAuthService.notifyEmailMfaSetup();
  }

  register(user: RegistrationData): Observable<SUCCESS_TYPE> {
    return this.vendorAuthService.register(user);
  }

  resendConfirmationMail(username: string): Observable<SUCCESS_TYPE> {
    return this.vendorAuthService.resendConfirmationMail(username);
  }

  sendMFACode(confirmationCode: string, username: string, mfaOption: string) {
    return this.interceptSessionChangeAndStartTimer(
      this.vendorAuthService.sendMFACode(confirmationCode, username, mfaOption),
      SessionEvent.LOGIN_COMPLETE);
  }

  resendEmailMfa(): Observable<SUCCESS_TYPE> {
    return this.vendorAuthService.resendEmailMfa();
  }

  associateEmailToken(): Observable<SUCCESS_TYPE> {
    return this.vendorAuthService.associateEmailToken();
  }

  verifyEmailToken(code: string): Observable<string> {
    return this.vendorAuthService.verifyEmailToken(code);
  }

  getUserSessionToken(): Observable<string> {
    return this.vendorAuthService.getUserSessionToken();
  }

  verifySoftwareToken(token: string): Observable<unknown> {
    return this.vendorAuthService.verifySoftwareToken(token);
  }

  private getApplicableService(): Observable<SsoService | AuthService> {
    return this.isAuthenticated()
      .pipe(map(sessionChangedData => sessionChangedData?.sso ? this.ssoService : this.vendorAuthService));
  }

  publishReloadLoginEvent(): void {
    this.broadCastService.publish(SessionEvent.INSTANT_LOGIN_RELOAD, null);
  }

  private interceptSessionChangeAndStartTimer<T extends SessionChangedData | null>(
    data: Observable<T>,
    sessionEvent: SessionEvent,
    isRemote = false
  ): Observable<T> {
    const isLoginOrLogout = [
      SessionEvent.LOGIN_COMPLETE,
      SessionEvent.LOGOUT_COMPLETE,
      SessionEvent.LOGOUT_SESSION_EXPIRED].includes(sessionEvent);

    return data
      .pipe(
        tap(sessionChangedData => {
          if (sessionMetadata.changesInProgress) {
            return;
          }

          sessionMetadata.changesInProgress = true;
          if (sessionMetadata.tokenExpiresAt !== sessionChangedData?.expiresAt) {
            const timerTriggerEpochSeconds = !sessionChangedData?.timerTriggerEpochSeconds
              ? this.computeTimerTriggerEpochSeconds(sessionChangedData)
              : sessionChangedData.timerTriggerEpochSeconds;

            this.deleteSessionMetaIfExists(isLoginOrLogout);

            if (sessionChangedData?.expiresAt) {
              sessionMetadata.tokenExpiresAt = sessionChangedData.expiresAt;
              sessionMetadata.refreshTriggerAt = timerTriggerEpochSeconds;
              sessionMetadata.isSso = sessionChangedData.sso;

              this.createRefreshSessionTimer(timerTriggerEpochSeconds);
              this.timeoutService.startListening();
            }
          }

          if (sessionEvent && !isRemote) {
            if (sessionChangedData) {
              sessionChangedData.timerTriggerEpochSeconds = sessionMetadata.refreshTriggerAt;
            }
            this.broadCastService.publish(sessionEvent, sessionChangedData);
          }

          sessionMetadata.changesInProgress = false;
        })
      );
  }

  private computeTimerTriggerEpochSeconds(sessionChangedData: SessionChangedData): number | null {
    if (!sessionChangedData?.timestampSeconds) {
      return null;
    }

    const clientTimeDriftSeconds = Math.round(new Date().getTime() / 1000 - sessionChangedData.timestampSeconds);
    return sessionChangedData.expiresAt - sessionRenewalThreshold - multicastWaitTime + clientTimeDriftSeconds;
  }

  private createRefreshSessionTimer(timerTriggerEpochSeconds: number): void {
    sessionMetadata.refreshTimer = timer(new Date(TimeUtils.secondsToMillis(timerTriggerEpochSeconds)))
      .pipe(
        tap(() => this.broadCastService.publish(SessionEvent.REFRESH_INTENTION)),
        delay(TimeUtils.secondsToMillis(multicastWaitTime))
      )
      .subscribe(
        () => {
          if (this.isCurrentWindowElectedToRefresh()) {
            this.broadCastService.publish(SessionEvent.REFRESH_STARTED);
            this.refreshSession().subscribe();
          }
        }
      );
  }

  private isCurrentWindowElectedToRefresh(): boolean {
    const advisedWindowUuids = sessionMetadata.advisedWindowUuids || [];
    const currentWindowUuid = BroadCastService.getCurrentWinUuid();

    if (!currentWindowUuid || advisedWindowUuids.length === 0) {
      return true;
    }

    return advisedWindowUuids.sort()[0] > currentWindowUuid;
  }

  private deleteSessionMetaIfExists(isLoginOrLogout: boolean) {
    if (sessionMetadata.refreshTimer) {
      sessionMetadata.refreshTimer.unsubscribe();
    }
    sessionMetadata.tokenExpiresAt = undefined;
    sessionMetadata.refreshTriggerAt = undefined;
    sessionMetadata.refreshTimer = undefined;
    sessionMetadata.isSso = undefined;
    sessionMetadata.advisedWindowUuids = [];

    if (isLoginOrLogout) {
      this.timeoutService.stopListening();
      this.dialog.closeAll();
      this.accDataService.resetAccountsState();
      this.accDataService.clearPrefetchedAccountData();
      this.pendingAuthenticationCheck$ = null;
      CacheUtils.evict();
    }
  }

  private initializeBroadcastChannelListeners() {
    this.broadCastService.messagesOfType(SessionEvent.LOGIN_COMPLETE).subscribe(
      crossWindowMessage => this.handleRemoteSessionEvent(crossWindowMessage, '/', true));
    this.broadCastService.messagesOfType(SessionEvent.REFRESH_INTENTION).subscribe(
      crossWindowMessage => this.collectAdvisedWindowUuid(crossWindowMessage.sourceWindowUuid));
    this.broadCastService.messagesOfType(SessionEvent.REFRESH_STARTED).subscribe(
      () => sessionMetadata.refreshTimer && sessionMetadata.refreshTimer.unsubscribe());
    this.broadCastService.messagesOfType(SessionEvent.REFRESH_COMPLETE).subscribe(
      crossWindowMessage => this.handleRemoteSessionEvent(crossWindowMessage));
    this.broadCastService.messagesOfType(SessionEvent.LOGOUT_COMPLETE).subscribe(
      crossWindowMessage => this.handleRemoteSessionEvent(crossWindowMessage, '/login'));
    this.broadCastService.messagesOfType(SessionEvent.LOGOUT_SESSION_EXPIRED).subscribe(
      crossWindowMessage => this.handleRemoteSessionEvent(crossWindowMessage, '/login?sessionExpired=true'));
    this.broadCastService.messagesOfType(SessionEvent.INSTANT_LOGIN_RELOAD).subscribe(
      crossWindowMessage => this.handleRemoteSessionEvent(crossWindowMessage, '/login'));

  }

  private handleRemoteSessionEvent(crossWindowMessage: DcpCrossWindowMessage, path: string = null, instantRefreshTabs: boolean = false) {
    if (!crossWindowMessage || !crossWindowMessage.event) {
      return;
    }

    if (crossWindowMessage.event === SessionEvent.INSTANT_LOGIN_RELOAD) {
      return;
    }
    this.interceptSessionChangeAndStartTimer(
      of(<SessionChangedData>crossWindowMessage.payload),
      <SessionEvent>crossWindowMessage.event,
      true).subscribe(() => {
      if (path) {
        this.zone.run(() => {
          this.router.navigateByUrl(path).then();
        });
      }
    });

    if (!IframeService.runsInIframe() && instantRefreshTabs) {
      window.location.reload();
    }
  }

  private collectAdvisedWindowUuid(windowUuid: string) {
    if (!windowUuid) {
      return;
    }

    if (!sessionMetadata.advisedWindowUuids) {
      sessionMetadata.advisedWindowUuids = [];
    }

    sessionMetadata.advisedWindowUuids.push(windowUuid);
  }
}
